import { Injectable } from '@angular/core';
import { MarkedOptions, MarkedRenderer } from 'ngx-markdown';
import { escapeHtml } from './core.utils.service';

// Untyped CJS module (same require() pattern used for @toast-ui/editor elsewhere in this codebase).
// CSS for these (prism theme/plugins, katex fonts) is pulled in globally via
// core/css/gforge-app-bundle.css, matching how the ToastUI editor components' own
// CSS is wired in rather than importing styles from a .ts file.
//
// Must run BEFORE any of the component/plugin side-effect imports below: those
// files reference the bare `Prism` global directly (no import of their own), so
// prismjs's core needs to have already set it or they throw ReferenceError.
const Prism: any = require('prismjs');

import 'prismjs/components/prism-json';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-rust';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-markup-templating';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-markdown';
import 'prismjs/components/prism-docker';
import 'prismjs/plugins/line-numbers/prism-line-numbers';
import 'prismjs/plugins/line-highlight/prism-line-highlight';
import 'prismjs/plugins/toolbar/prism-toolbar';
import 'prismjs/plugins/copy-to-clipboard/prism-copy-to-clipboard';
import { marker } from './ngx-translate-extract-marker';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const mermaidModule: any = require('mermaid');
const mermaid: any = mermaidModule.default || mermaidModule;
mermaid.initialize({ startOnLoad: false, theme: 'default' });

// Mermaid's internal render queue can reject independently of the promise
// returned by `mermaid.render()` (which GfCodeBlockView.renderMermaid and
// renderMermaidBlocks below already .catch() correctly) — the same parse
// error then also surfaces a second time as an unhandled rejection with no
// promise of ours attached to it. Suppress only rejections shaped like
// mermaid's own error objects ({ str, hash, message, error }), so unrelated
// app errors still surface normally.
if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const reason = event.reason;
        if (reason && typeof reason === 'object' && 'hash' in reason && 'str' in reason && 'error' in reason) {
            event.preventDefault();
        }
    });
}

// eslint-disable-next-line @typescript-eslint/no-var-requires
const renderMathInElementModule: any = require('katex/dist/contrib/auto-render.js');
const renderMathInElement: any = renderMathInElementModule.default || renderMathInElementModule;

// Core katex (renderToString), used directly by the WYSIWYG math decoration
// plugin — see buildMathWysiwygDecorations / renderMathWidget below. Distinct
// from the auto-render module above, which only exposes a DOM-mutating
// "render into this element" API, not a plain string-in/HTML-out one.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const katexModule: any = require('katex');
const katex: any = katexModule.default || katexModule;

// eslint-disable-next-line @typescript-eslint/no-var-requires
const ClipboardModule: any = require('clipboard');
const Clipboard: any = ClipboardModule.default || ClipboardModule;

const KATEX_DELIMITERS = [
    { left: '$$', right: '$$', display: true },
    { left: '$', right: '$', display: false },
];

interface MathSegment {
    type: 'text' | 'math';
    data: string;
    rawData?: string;
    display?: boolean;
}

function findEndOfMath(delimiter: string, text: string, startIndex: number): number {
    let index = startIndex;
    let braceLevel = 0;
    const delimLength = delimiter.length;
    while (index < text.length) {
        const character = text[index];
        if (braceLevel <= 0 && text.slice(index, index + delimLength) === delimiter) {
            return index;
        } else if (character === '\\') {
            index++;
        } else if (character === '{') {
            braceLevel++;
        } else if (character === '}') {
            braceLevel--;
        }
        index++;
    }
    return -1;
}

function escapeRegexForDelimiter(value: string): string {
    return value.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
}

const AMS_BEGIN_REGEX = /^\\begin\{/;

/**
 * Ported from katex/contrib/auto-render's internal `splitAtDelimiters` (same
 * left/right-delimiter, brace-nesting, and backslash-escaping semantics as
 * the static preview pane's math rendering, so both panes treat ambiguous
 * cases — e.g. literal currency like "$5 and $10" — identically) minus its
 * DOM mutation: that module has no public API for just finding delimiter
 * matches, only "find and replace directly in this element's DOM," which is
 * exactly the WYSIWYG-corrupting pattern this decoration plugin exists to
 * avoid. We only need the split to compute decoration ranges.
 */
function splitAtMathDelimiters(text: string): MathSegment[] {
    const data: MathSegment[] = [];
    const regexLeft = new RegExp('(' + KATEX_DELIMITERS.map((d) => escapeRegexForDelimiter(d.left)).join('|') + ')');
    let remaining = text;
    while (true) {
        const index = remaining.search(regexLeft);
        if (index === -1) break;
        if (index > 0) {
            data.push({ type: 'text', data: remaining.slice(0, index) });
            remaining = remaining.slice(index);
        }
        const delimiter = KATEX_DELIMITERS.find((d) => remaining.startsWith(d.left));
        if (!delimiter) break;
        const endIndex = findEndOfMath(delimiter.right, remaining, delimiter.left.length);
        if (endIndex === -1) break;
        const rawData = remaining.slice(0, endIndex + delimiter.right.length);
        const math = AMS_BEGIN_REGEX.test(rawData) ? rawData : remaining.slice(delimiter.left.length, endIndex);
        data.push({ type: 'math', data: math, rawData, display: delimiter.display });
        remaining = remaining.slice(endIndex + delimiter.right.length);
    }
    if (remaining !== '') {
        data.push({ type: 'text', data: remaining });
    }
    return data;
}

interface MarkdownInsertAfterModeSwitchOptions {
    editor: any;
    pastedText: string;
    existingMarkdown: string;
    remainingFrames: number;
    isDestroyed: () => boolean;
    isEditorInMarkdownMode: () => boolean;
    setUpdating: (value: boolean) => void;
    onAfterInsert?: () => void;
    onError?: (error: any) => void;
}

interface CleanupBrTagsAfterModeSwitchOptions {
    editor: any;
    isDestroyed: () => boolean;
    isUpdating: () => boolean;
    setUpdating: (value: boolean) => void;
    onError?: (error: any) => void;
}

/**
 * Which button set a host wants from {@link MarkdownEditorService.buildToolbarItems}.
 *
 * - `full` — document-scale editors (wiki page, notebook): headings, block
 *   elements and list indentation on top of the essential set.
 * - `essential` — constrained sections (tracker/forum/FRS comment boxes, the
 *   chat compose box): inline formatting, lists, insert and code only, so the
 *   toolbar still fits a box that is a couple of lines tall.
 */
type MarkdownToolbarVariant = 'full' | 'essential';

interface MarkdownToolbarOptions {
    variant: MarkdownToolbarVariant;
    /**
     * Adds the custom "Insert Video" button; the host must register an
     * `insertVideo` command. Honoured only by the `full` variant — inserting a
     * video is document-scale work, not something a comment or compose box
     * needs, so a constrained toolbar never carries it however the host asks.
     */
    video?: boolean;
    /** Adds the custom "Insert Attachment" button; the host must register an `insertAttachment` command. */
    attachment?: boolean;
    /** Adds ToastUI's scroll-sync toggle. Only meaningful with a side-by-side preview. */
    scrollSync?: boolean;
    /** Adds the custom Save button; the host must register a `save` command. */
    save?: boolean;
    /** Host's translate function, used for the custom buttons' tooltips. Identity when omitted. */
    translate?: (key: string) => string;
}

interface HandleMarkdownPasteOptions<TMode extends string> {
    event: ClipboardEvent;
    editor: any;
    readOnly: boolean;
    currentMode: TMode;
    markdownMode: TMode;
    isUpdating: boolean;
    isDestroyed: () => boolean;
    isEditorInMarkdownMode: () => boolean;
    setUpdating: (value: boolean) => void;
    switchToMarkdownMode: () => void;
    onAfterInsert?: () => void;
    onModeSwitchError?: (error: any) => void;
    onInsertError?: (error: any) => void;
}

const WW_CODE_BLOCK_WRAPPER_CLASS = 'toastui-editor-ww-code-block';
const MERMAID_RENDER_DEBOUNCE_MS = 350;

/** Matches an optional language token followed by a `{1,3-5}` line range. */
const LINE_HIGHLIGHT_INFO_REGEX = /^(\S*?)\s*\{\s*([\d,\s-]+)\}\s*$/;

/**
 * Split a fenced-code-block info string — a markdown fence's raw info string
 * (`node.info` in {@link MarkdownEditorService.buildCodeBlockRenderer}, or
 * `infostring` in {@link gfMarkedOptionsFactory}'s renderer override), or the
 * WYSIWYG `codeBlock` node's `language` attr, which ToastUI populates from
 * that same raw info string — into its language token and an optional Prism
 * Line Highlight range: the ```language {1,3-5}``` suffix `GfFencedCodeRenderer`
 * (WikiRenderingService.php) recognizes on the backend, so the live editor
 * preview and the saved wiki/notebook/tracker-item page all highlight the
 * same lines. Falls back to treating the whole trimmed string as the language
 * when there's no `{...}` suffix, matching every pre-existing bare-language
 * info string unchanged.
 */
export function parseFenceInfo(info: string): { language: string; lines: string | null } {
    const trimmed = (info || '').trim();
    const match = trimmed.match(LINE_HIGHLIGHT_INFO_REGEX);
    if (match) {
        return { language: match[1].toLowerCase(), lines: match[2].replace(/\s+/g, '') };
    }
    return { language: trimmed.split(/\s+/)[0].toLowerCase(), lines: null };
}

/**
 * Class prefix that carries a Prism Line Highlight range past Angular's HTML
 * sanitizer, alongside the `data-line` attribute Prism actually reads.
 *
 * Every `marked`-rendered surface runs its HTML through `DomSanitizer` at
 * `SecurityContext.HTML` before it reaches the DOM — ngx-markdown's
 * `MarkdownService.parse()` does it for `<markdown>` (notebook pages, chat),
 * and `GfMarkdownViewerComponent.render()` does it explicitly for
 * `gf-markdown-viewer` (tracker items, comments), calling it out as the trust
 * boundary for client-rendered markdown. That sanitizer drops every attribute
 * outside its own allow-list, and `data-*` is not on it — so the `data-line`
 * attribute below never survived to the DOM on any of those surfaces, which is
 * exactly why line highlighting worked in the wiki view (backend HTML, assigned
 * straight to `innerHTML`) and in the editors' preview panes (ToastUI writes its
 * own innerHTML) but nowhere else. `class` IS on the allow-list, so the range
 * rides along as a class too and {@link MarkdownEditorService.enhanceStaticHtml}
 * turns it back into `data-line` before Prism runs. Weakening the sanitizer
 * instead would trade a rendering nicety for the trust boundary.
 */
const LINE_HIGHLIGHT_CLASS_PREFIX = 'gf-line-highlight-';

/**
 * `MARKED_OPTIONS` factory for `MarkdownModule.forRoot()` (ngx-markdown), shared by
 * every module that renders notebook/tracker-item markdown via the `<markdown>`
 * component (`marked`, not the ToastUI/CommonMark paths the rest of this file
 * covers). Overrides only the `code` token renderer, to recognize the same
 * ```language {1,3-5}``` Prism Line Highlight suffix as the wiki page's
 * `GfFencedCodeRenderer` and the editor's own `buildCodeBlockRenderer` — emitting
 * a `data-line` attribute on the `<pre>` tag (plus the sanitizer-proof
 * {@link LINE_HIGHLIGHT_CLASS_PREFIX} class) when a range is present. Delegates to
 * `marked`'s own default `code` renderer for the HTML itself (language class,
 * escaping) rather than reimplementing it, so output for every fence WITHOUT a
 * line range is byte-for-byte unchanged from stock `marked` behavior.
 *
 * The parsed language, not the raw info string, is what gets handed to that
 * default renderer: `marked` derives the `language-xxx` class from the first
 * whitespace-delimited token, which for a language-less ```` ``` {2} ```` fence is
 * the range itself — a bogus `class="language-{2}"`. `GfFencedCodeRenderer`
 * (WikiRenderingService.php) emits no class at all there, and this keeps the two
 * renderers producing the same HTML for the same fence.
 */
export function gfMarkedOptionsFactory(): MarkedOptions {
    const renderer = new MarkedRenderer();
    const defaultCode = renderer.code.bind(renderer);

    renderer.code = (code: string, infostring: string | undefined, escaped: boolean) => {
        const { language, lines } = parseFenceInfo(infostring || '');
        const html = defaultCode(code, language, escaped);
        // `lines` is `[\d,-]+` by construction (see LINE_HIGHLIGHT_INFO_REGEX,
        // whitespace stripped), so it needs no escaping in either position.
        return lines
            ? html.replace(/^<pre>/, `<pre data-line="${lines}" class="${LINE_HIGHLIGHT_CLASS_PREFIX}${lines}">`)
            : html;
    };

    return { renderer, gfm: true };
}

/**
 * Return a copy of ToastUI's own codeBlock HTML AST (the array `context.origin()`
 * returns — see `buildCodeBlockRenderer`) with a `data-line` attribute added to its
 * `<pre>` opening tag, so the Markdown preview pane highlights the same lines
 * `GfFencedCodeRenderer` highlights in the saved wiki page. Never mutates the
 * array or objects `origin()` returned — only ever builds new ones — regardless
 * of whether ToastUI happens to hand back freshly-built objects today.
 */
function applyLineHighlightAttribute(origin: any, lines: string): any {
    if (!Array.isArray(origin)) return origin;
    return origin.map((item: any) =>
        item && item.type === 'openTag' && item.tagName === 'pre'
            ? { ...item, attributes: { ...(item.attributes || {}), 'data-line': lines } }
            : item,
    );
}

/**
 * ProseMirror NodeView for the WYSIWYG `codeBlock` node, registered by
 * {@link MarkdownEditorService.buildPlugins}. Reproduces ToastUI's own default
 * `CodeBlockView` wrapper/pre/code DOM shape (minus the click-to-rename
 * language badge, deliberately dropped — see the CSS that hides its native
 * `:after` pseudo-element, added because it visually collided with the copy
 * button) so ordinary code blocks are otherwise unchanged, and adds two
 * things the default view doesn't have:
 *
 * - A live-rendered Mermaid preview for `language === 'mermaid'` blocks,
 *   drawn into an element that is a sibling of (never inside) `contentDOM`.
 *   ProseMirror only treats mutations inside `contentDOM` as user input it
 *   needs to fold back into the document; anything outside it (this preview,
 *   the copy button) is invisible to that mechanism. The mermaid fence text
 *   itself stays in a normal, real `contentDOM` — just visually hidden via
 *   CSS — so it's still the actual, losslessly-tracked document content;
 *   editing it is only possible from Raw/Markdown mode, matching the
 *   pre-existing UX (the old trick couldn't be edited from WYSIWYG either).
 * - A Clipboard.js-backed copy button, created once per block outside
 *   `contentDOM`, replacing the old post-hoc `addCopyButtons` DOM sweep.
 */
class GfCodeBlockView {
    public dom!: HTMLElement;
    public contentDOM: HTMLElement | null = null;

    private static mermaidIdCounter = 0;

    private node: any;
    private mermaidPreviewEl: HTMLElement | null = null;
    private mermaidId: string;
    private mermaidRenderToken = 0;
    private mermaidRenderTimer: any = null;
    private destroyed = false;

    constructor(node: any, _view: any, _getPos: () => number, private ensureClipboard: () => void) {
        this.node = node;
        this.mermaidId = 'gf-mermaid-' + GfCodeBlockView.mermaidIdCounter++;
        this.createElement();
        if (this.isMermaid()) {
            this.renderMermaidWhenConnected();
        }
    }

    /**
     * ProseMirror creates a NodeView's `dom` in its constructor, then inserts it
     * into the real document afterward — not necessarily in the same tick. A
     * detached element has no real layout, so measuring text against it (which
     * is exactly what mermaid.render's container argument is for — see
     * renderMermaid) returns zero/garbage sizes. Diagrams that lean heavily on
     * precise measurement (long `foreignObject`-wrapped labels, class diagrams'
     * fitted text boxes) visibly break; simpler ones happen to tolerate it,
     * which is why this only showed up for some diagram types. Wait until the
     * preview element is actually connected before the first real render.
     */
    private renderMermaidWhenConnected(): void {
        if (this.destroyed) return;
        if (this.mermaidPreviewEl?.isConnected) {
            this.renderMermaid();
            return;
        }
        requestAnimationFrame(() => this.renderMermaidWhenConnected());
    }

    private isMermaid(): boolean {
        return parseFenceInfo(this.node.attrs.language).language === 'mermaid';
    }

    private createElement(): void {
        const language = this.node.attrs.language;
        const wrapper = document.createElement('div');
        wrapper.setAttribute('data-language', language || 'text');
        wrapper.className = WW_CODE_BLOCK_WRAPPER_CLASS;

        const pre = document.createElement('pre');
        const code = document.createElement('code');
        if (language) {
            code.setAttribute('data-language', language);
        }

        pre.classList.add('language-' + (parseFenceInfo(language || '').language || 'none'));
        pre.appendChild(code);

        if (this.isMermaid()) {
            const preview = document.createElement('div');
            preview.className = 'gf-mermaid-preview';
            wrapper.appendChild(preview);
            this.mermaidPreviewEl = preview;
            pre.classList.add('gf-mermaid-source');
        }
        wrapper.appendChild(pre);

        const copyButton = document.createElement('button');
        copyButton.type = 'button';
        copyButton.className = 'gf-code-copy-btn';
        copyButton.textContent = 'Copy';
        copyButton.contentEditable = 'false';
        wrapper.appendChild(copyButton);
        this.ensureClipboard();

        this.dom = wrapper;
        this.contentDOM = code;
    }

    private scheduleMermaidRender(): void {
        if (this.mermaidRenderTimer) {
            clearTimeout(this.mermaidRenderTimer);
        }
        this.mermaidRenderTimer = setTimeout(() => this.renderMermaid(), MERMAID_RENDER_DEBOUNCE_MS);
    }

    private renderMermaid(): void {
        if (!this.mermaidPreviewEl) return;
        const text = this.node.textContent;
        const token = ++this.mermaidRenderToken;
        const id = this.mermaidId + '-' + token;
        // Pass our own (real, attached, correctly-styled) preview element as the
        // measurement container rather than letting mermaid default to a
        // temporary element elsewhere in the document (commonly `document.body`).
        // Mermaid measures actual text/font metrics to lay a diagram out — if
        // that measurement happens in a context with different font-family/size
        // than where the SVG is ultimately displayed, node boxes end up sized
        // for the wrong glyphs and text renders shifted/clipped relative to them.
        Promise.resolve()
            .then(() => mermaid.render(id, text, this.mermaidPreviewEl))
            .then(({ svg }: { svg: string }) => {
                if (this.destroyed || token !== this.mermaidRenderToken || !this.mermaidPreviewEl) return;
                this.mermaidPreviewEl.classList.remove('gf-mermaid-error');
                this.mermaidPreviewEl.innerHTML = svg;
            })
            .catch((error: any) => {
                if (this.destroyed || token !== this.mermaidRenderToken || !this.mermaidPreviewEl) return;
                console.warn('MarkdownEditorService: mermaid WYSIWYG render failed', error);
                this.mermaidPreviewEl.classList.add('gf-mermaid-error');
                this.mermaidPreviewEl.textContent = 'Invalid Mermaid diagram';
            });
    }

    public update(node: any): boolean {
        if (node.type !== this.node.type) {
            return false;
        }
        const textChanged = node.textContent !== this.node.textContent;
        const languageChanged = node.attrs.language !== this.node.attrs.language;
        this.node = node;
        if (languageChanged) {
            // Language changes affect the whole wrapper shape (mermaid preview
            // element present or not) — simplest and safest to have ProseMirror
            // tear down and recreate this view rather than try to patch it.
            return false;
        }
        if (this.isMermaid() && textChanged) {
            this.scheduleMermaidRender();
        }
        return true;
    }

    public stopEvent(): boolean {
        return true;
    }

    /**
     * Without this override, ProseMirror's default behavior for a NodeView that
     * defines `contentDOM` is to NOT ignore mutations elsewhere in `dom` (it only
     * auto-ignores everything when there's no `contentDOM` at all). The Mermaid
     * preview (`innerHTML = svg`) and the copy button (class/text toggled on
     * click) both mutate `dom` outside `contentDOM` — without this guard,
     * ProseMirror tries to reconcile those mutations against this node's
     * `text*`-only schema, which is exactly the kind of thing that corrupts or
     * crashes the view. Only mutations actually inside `contentDOM` are real
     * document edits; everything else here is view-only and safe to ignore.
     */
    public ignoreMutation(mutation: MutationRecord | { type: 'selection'; target: Element }): boolean {
        if (!this.contentDOM) return true;
        return !this.contentDOM.contains(mutation.target as Node);
    }

    public destroy(): void {
        this.destroyed = true;
        if (this.mermaidRenderTimer) {
            clearTimeout(this.mermaidRenderTimer);
        }
    }
}

@Injectable()
export class MarkdownEditorService {
    /** Lazily-created, shared Clipboard.js instance backing every copy button — see ensureClipboard. */
    private clipboard: any = null;

    /** Panes currently waiting (via a ResizeObserver) to become visible before Mermaid can render — see renderMermaidBlocks. */
    private mermaidPendingPanes = new WeakSet<HTMLElement>();

    /**
     * ToastUI `toolbarItems` shared by both markdown editors, so a button means
     * the same thing and sits in the same place everywhere in the product.
     *
     * Rows are emitted in a fixed order — headings, inline formatting, blocks,
     * lists, insert, code, scroll sync, save — and `variant` only decides
     * whether the document-scale rows are present, never where a button lands.
     * Everything beyond that (the custom video/attachment/save commands and
     * scroll sync) is opt-in per host, because each one needs a command the
     * host itself registers.
     *
     * Video is a `full`-variant button: see MarkdownToolbarOptions.video.
     *
     * Video and attachment ride in the insert row rather than trailing the bar.
     * ToastUI folds whatever does not fit into its "…" dropdown starting from
     * the END of the toolbar, so a trailing custom group is the first thing to
     * disappear on a narrow editor — which is exactly what happened to the
     * wiki's video button when the full variant made the bar longer. Sitting
     * with table/link/image also matches what they are: insert commands.
     *
     * @see MarkdownToolbarVariant for what `full` and `essential` cover.
     */
    public buildToolbarItems(options: MarkdownToolbarOptions): any[][] {
        const isFull = options.variant === 'full';
        const translate = options.translate || ((key: string) => key);

        const rows: any[][] = [];
        if (isFull) {
            rows.push(['heading']);
        }
        rows.push(['bold', 'italic', 'strike']);
        if (isFull) {
            rows.push(['hr', 'quote']);
        }
        rows.push(isFull ? ['ul', 'ol', 'task', 'indent', 'outdent'] : ['ul', 'ol', 'task']);
        const insertRow: any[] = ['table', 'link', 'image'];
        if (options.video && isFull) {
            insertRow.push(this.buildCustomToolbarButton('video', '▶', translate(marker('Insert Video')), 'insertVideo'));
        }
        if (options.attachment) {
            insertRow.push(
                this.buildCustomToolbarButton('attachment', '📎', translate(marker('Insert Attachment')), 'insertAttachment'),
            );
        }
        rows.push(insertRow);
        rows.push(['code', 'codeblock']);

        if (options.scrollSync) {
            rows.push(['scrollSync']);
        }
        if (options.save) {
            rows.push([this.buildCustomToolbarButton('save', '💾', translate(marker('Save (Ctrl+S)')), 'save')]);
        }

        return rows;
    }

    /**
     * Descriptor for a toolbar button backed by one of our own commands rather
     * than a built-in ToastUI one. `backgroundImage: none` opts out of ToastUI's
     * icon sprite so the `text` glyph shows instead; `<name>-icon` is the hook
     * the editors' stylesheets use to size/position it.
     */
    private buildCustomToolbarButton(name: string, text: string, tooltip: string, command: string): any {
        return {
            name,
            tooltip,
            className: `toastui-editor-toolbar-icons ${name}-icon`,
            // Sized to sit with the sprite icons, which the shared toolbar
            // stylesheet scales down from ToastUI's stock 32px buttons.
            style: { backgroundImage: 'none', fontSize: '11px' },
            text,
            command,
        };
    }

    /**
     * ToastUI `customHTMLRenderer.codeBlock` handler shared by both markdown editors.
     * Recognizes the same ```mermaid fence the final wiki/notebook view pages render
     * (via ngx-markdown), swapping it for a `<div class="mermaid">` block; every other
     * language falls back to ToastUI's own escaped `<pre><code>` output via `context.origin()`,
     * with a Prism Line Highlight `data-line` attribute spliced onto its `<pre>` when the
     * fence's info string carries a ```language {1,3-5}``` line range (see `parseFenceInfo`
     * and `applyLineHighlightAttribute` above) — the same syntax `GfFencedCodeRenderer`
     * (WikiRenderingService.php) recognizes on the backend, so the live Markdown preview
     * pane highlights the same lines the saved wiki page does.
     */
    public buildCodeBlockRenderer(): (node: any, context: any) => any {
        return (node: any, context: any) => {
            const { language, lines } = parseFenceInfo(node.info || '');
            if (language !== 'mermaid') {
                const origin = context && typeof context.origin === 'function' ? context.origin() : null;
                return lines ? applyLineHighlightAttribute(origin, lines) : origin;
            }
            return [
                { type: 'openTag', tagName: 'div', outerNewLine: true, attributes: { class: 'mermaid' } },
                { type: 'html', content: escapeHtml(node.literal || '') },
                { type: 'closeTag', tagName: 'div', outerNewLine: true },
            ];
        };
    }

    /**
     * ToastUI editor `plugins` array shared by both markdown editors.
     *
     * - Prism syntax highlighting for WYSIWYG code blocks via a ProseMirror
     *   decoration plugin — decorations are ProseMirror's own rendering layer,
     *   so it draws them itself and never mistakes that output for user input,
     *   unlike direct DOM mutation.
     * - KaTeX math rendering (inline, single-line display, and multi-paragraph
     *   display runs) via a second decoration plugin ({@link createMathDecorationPlugin}) —
     *   same reasoning: raw `$...$`/`$$...$$` source stays real, tracked,
     *   editable document text (CSS-hidden by the decoration), with the
     *   rendered formula laid on top as a widget decoration. Replaces the old
     *   `mergeDisplayMathAcrossParagraphs`, which mutated the live WYSIWYG DOM
     *   directly via `.textContent =` — ProseMirror's own DOM-observer read
     *   those mutations back as if the user had typed them, permanently
     *   corrupting the document (the "duplicated math" bug this replaces).
     * - A custom `codeBlock` NodeView ({@link GfCodeBlockView}) replacing the
     *   old customHTMLRenderer class-leak trick for Mermaid: it renders the
     *   diagram into its own preview element that lives outside `contentDOM`,
     *   so ProseMirror's mutation observer never sees the injected SVG and can
     *   never fold it back into the document as if the user had typed it — the
     *   actual mechanism behind the "duplicated math" / permanently-corrupted
     *   document bug diagnosed earlier. It also owns the copy button (created
     *   once per block, also outside `contentDOM`) instead of a separate
     *   post-hoc DOM sweep. It's otherwise the same wrapper shape ToastUI's own
     *   default `CodeBlockView` produces for every non-Mermaid language, minus
     *   the click-to-rename language badge (its native `:after` pseudo-element
     *   is hidden via CSS — it visually collided with the copy button).
     *
     * Note the official `@toast-ui/editor-plugin-code-syntax-highlight` plugin
     * was tried earlier instead of building our own — its own NodeView broke
     * the Mermaid trick and copy button. Its decoration technique was still a
     * good reference (see `createPrismDecorationPlugin` below); its NodeView
     * (which we don't need — the decoration plugin alone handles highlighting)
     * is not reused.
     */
    public buildPlugins(): any[] {
        return [(context: any) => this.buildWysiwygEnhancementsPlugin(context)];
    }

    private buildWysiwygEnhancementsPlugin(context: any): { wysiwygPlugins: any[]; wysiwygNodeViews: any } {
        const pmState = context.pmState;
        const pmView = context.pmView;
        return {
            wysiwygPlugins: [
                () => this.createPrismDecorationPlugin(pmState, pmView),
                () => this.createMathDecorationPlugin(pmState, pmView),
            ],
            wysiwygNodeViews: {
                codeBlock: (node: any, view: any, getPos: () => number) =>
                    new GfCodeBlockView(node, view, getPos, () => this.ensureClipboard()),
            },
        };
    }

    private createPrismDecorationPlugin(pmState: any, pmView: any): any {
        const getDecorations = (doc: any) => this.buildPrismWysiwygDecorations(doc, pmView);
        return new pmState.Plugin({
            state: {
                init: (_config: any, state: any) => getDecorations(state.doc),
                apply: (tr: any, decorationSet: any) =>
                    tr.docChanged ? getDecorations(tr.doc) : decorationSet.map(tr.mapping, tr.doc),
            },
            props: {
                decorations: function (this: any, state: any) {
                    return this.getState(state);
                },
            },
        });
    }

    private buildPrismWysiwygDecorations(doc: any, pmView: any): any {
        const decorations: any[] = [];
        doc.descendants((node: any, pos: number) => {
            if (node.type.name !== 'codeBlock') return;
            const language = parseFenceInfo(node.attrs.language).language;
            const grammar = Prism.languages[language];
            if (!grammar) return;
            let start = pos + 1;
            this.tokensToTextRuns(Prism.tokenize(node.textContent, grammar)).forEach(({ text, classes }) => {
                const end = start + text.length;
                if (classes.length) {
                    decorations.push(pmView.Decoration.inline(start, end, { class: classes.join(' ') }));
                }
                start = end;
            });
        });
        return pmView.DecorationSet.create(doc, decorations);
    }

    /**
     * Flatten Prism's (possibly nested) token tree into ordered `{ text, classes }`
     * runs, accumulating each ancestor token's `token`/`<type>`/alias class names
     * on the way down — mirrors Prism's own `Prism.Token.prototype.toString` /
     * the official code-syntax-highlight plugin's `parseTokens`, since decorations
     * need the same flat run list that HTML serialization would produce.
     */
    private tokensToTextRuns(tokens: any, classNames: string[] = []): Array<{ text: string; classes: string[] }> {
        const runs: Array<{ text: string; classes: string[] }> = [];
        const items = Array.isArray(tokens) ? tokens : [tokens];
        items.forEach((token: any) => {
            if (typeof token === 'string') {
                runs.push({ text: token, classes: classNames });
                return;
            }
            const classes = [
                ...classNames,
                ...(token.type ? ['token', token.type] : []),
                ...(token.alias ? (Array.isArray(token.alias) ? token.alias : [token.alias]) : []),
            ];
            runs.push(...this.tokensToTextRuns(token.content, classes));
        });
        return runs;
    }

    /**
     * ProseMirror decoration plugin rendering KaTeX math (inline `$...$`,
     * single-line `$$...$$`, and multi-paragraph `$$` / ...lines... / `$$`
     * runs) in the WYSIWYG pane. Same shape as `createPrismDecorationPlugin`
     * above, and the same reasoning applies: decorations are ProseMirror's own
     * rendering layer, so raw source stays real, tracked, editable document
     * text (just visually hidden via a CSS class on the decoration) while the
     * rendered formula is a separate widget decoration laid on top — nothing
     * is ever written into the document, unlike the old
     * `mergeDisplayMathAcrossParagraphs`/`.textContent =` approach, which is
     * what let ProseMirror's own DOM-observer misread our rendered KaTeX
     * output as user-typed text and permanently corrupt the document (see the
     * "duplicated math" investigation this replaces).
     */
    private createMathDecorationPlugin(pmState: any, pmView: any): any {
        const getDecorations = (doc: any) => this.buildMathWysiwygDecorations(doc, pmView);
        return new pmState.Plugin({
            state: {
                init: (_config: any, state: any) => getDecorations(state.doc),
                apply: (tr: any, decorationSet: any) =>
                    tr.docChanged ? getDecorations(tr.doc) : decorationSet.map(tr.mapping, tr.doc),
            },
            props: {
                decorations: function (this: any, state: any) {
                    return this.getState(state);
                },
            },
        });
    }

    private buildMathWysiwygDecorations(doc: any, pmView: any): any {
        const decorations: any[] = [];

        // Multi-paragraph `$$` / ...lines... / `$$` runs, top-level paragraphs
        // only (mirrors the old mergeDisplayMathAcrossParagraphs' scope) —
        // ToastUI's WYSIWYG conversion turns every soft line break inside what
        // was one markdown paragraph into a separate sibling <p> node, so this
        // pattern can't be caught by scanning any single node's text alone.
        const paragraphs: Array<{ node: any; pos: number }> = [];
        doc.forEach((node: any, offset: number) => {
            if (node.type.name === 'paragraph') {
                paragraphs.push({ node, pos: offset });
            }
        });
        for (let i = 0; i < paragraphs.length; i++) {
            if (paragraphs[i].node.textContent.trim() !== '$$') continue;
            for (let j = i + 1; j < paragraphs.length; j++) {
                if (paragraphs[j].node.textContent.trim() !== '$$') continue;
                const run = paragraphs.slice(i, j + 1);
                const formula = run
                    .slice(1, -1)
                    .map(({ node }) => node.textContent)
                    .join('\n');
                run.forEach(({ node, pos }) => {
                    decorations.push(pmView.Decoration.node(pos, pos + node.nodeSize, { class: 'gf-math-source-hidden' }));
                });
                decorations.push(
                    pmView.Decoration.widget(run[0].pos, () => this.renderMathWidget(formula, true), { side: -1 }),
                );
                i = j;
                break;
            }
        }

        // Inline `$...$` and single-line `$$...$$` within any text-bearing
        // node. A paragraph fully consumed by the run above (its trimmed text
        // is exactly "$$", or it's a bare formula line with no literal `$` at
        // all) can never also match here — splitAtMathDelimiters finds no
        // complete delimiter pair in either case — so there's no double
        // handling to guard against.
        doc.descendants((node: any, pos: number) => {
            if (node.type.name === 'codeBlock') return false;
            if (!node.isTextblock) return;
            const text = node.textContent;
            if (!text.includes('$')) return;
            const segments = splitAtMathDelimiters(text);
            if (segments.length === 1 && segments[0].type === 'text') return;
            let offset = 0;
            segments.forEach((segment) => {
                if (segment.type === 'math' && segment.rawData) {
                    const start = pos + 1 + offset;
                    const end = start + segment.rawData.length;
                    decorations.push(pmView.Decoration.inline(start, end, { class: 'gf-math-source-hidden' }));
                    decorations.push(
                        pmView.Decoration.widget(
                            start,
                            () => this.renderMathWidget(segment.data, !!segment.display),
                            { side: 1 },
                        ),
                    );
                    offset += segment.rawData.length;
                } else {
                    offset += segment.data.length;
                }
            });
        });

        return pmView.DecorationSet.create(doc, decorations);
    }

    private renderMathWidget(formula: string, displayMode: boolean): HTMLElement {
        const container = document.createElement(displayMode ? 'div' : 'span');
        container.className = displayMode ? 'gf-math-widget gf-math-widget-block' : 'gf-math-widget gf-math-widget-inline';
        container.contentEditable = 'false';
        try {
            container.innerHTML = katex.renderToString(formula, { displayMode, throwOnError: false });
        } catch (e) {
            container.classList.add('gf-math-widget-error');
            container.textContent = formula;
            console.warn('MarkdownEditorService: KaTeX WYSIWYG render failed', e);
        }
        return container;
    }

    /**
     * Post-process every rendered ToastUI content pane (markdown preview and/or
     * WYSIWYG view — both use the `.toastui-editor-contents` class) found under
     * `rootEl` with Prism syntax highlighting, Mermaid diagram rendering, and KaTeX
     * math rendering. Editor-specific — see {@link enhanceStaticHtml} for the
     * equivalent pass over a saved/displayed page's already-rendered HTML (wiki
     * view, notebook view, tracker-item markdown), which isn't a
     * `.toastui-editor-contents` pane and uses a different code-block class
     * convention.
     * Safe to call repeatedly (e.g. on every editor change) — each pass only acts
     * on unprocessed nodes.
     */
    public enhanceContainer(rootEl: HTMLElement | null | undefined): void {
        if (!rootEl) return;
        const panes = rootEl.querySelectorAll<HTMLElement>('.toastui-editor-contents');
        panes.forEach((pane) => this.enhancePane(pane));
    }

    private enhancePane(pane: HTMLElement): void {
        // The WYSIWYG pane IS the ProseMirror contenteditable root (ToastUI applies
        // the same `.toastui-editor-contents` class to it), unlike the static Markdown
        // preview pane. Code blocks there are a live NodeView, not the static HTML
        // `context.origin()` produces — running Prism directly against it would
        // overwrite ProseMirror's own `contentDOM`, so that pass is preview-only.
        // (WYSIWYG code blocks get highlighting/Mermaid/copy-button via the
        // ProseMirror plugin + NodeView registered in buildPlugins() instead.)
        const isWysiwyg = pane.isContentEditable;
        if (!isWysiwyg) {
            // Same language-less-fence normalization the read-only surfaces get
            // (see markPlainCodeBlocks) — without it the preview pane keeps
            // ToastUI's own <pre> styling for those blocks while the saved page
            // shows Prism's, which is the mismatch this preview exists to avoid.
            // No restoreLineHighlightRanges here: ToastUI writes this pane's HTML
            // itself, so its `data-line` never meets Angular's sanitizer.
            this.markPlainCodeBlocks(pane);
            this.applyLineNumbers(pane);
            try {
                Prism.highlightAllUnder(pane);
            } catch (e) {
                console.warn('MarkdownEditorService: Prism highlight failed', e);
            }
        }
        this.renderMermaidBlocks(pane);
        if (!isWysiwyg) {
            // WYSIWYG math (inline $...$, single-line $$...$$, and multi-paragraph
            // $$ / ...lines... / $$ runs) is handled entirely by the ProseMirror
            // decoration plugin registered in buildPlugins() — see
            // buildMathWysiwygDecorations. Decorations never touch real document
            // text, unlike this DOM-mutation-based pass, which is why it's
            // preview-pane-only now: the static preview pane isn't a live
            // ProseMirror document, so there's no document to corrupt here.
            //
            // The preview pane's toHTML softbreak convertor renders every
            // markdown newline as a literal `<br>` within one <p> — splitting a
            // `$$\nformula\n$$` block into sibling text nodes that KaTeX's
            // auto-render (delimiter-matching within a single text node) can't
            // bridge. Normalize that shape first.
            this.normalizeDisplayMathLineBreaks(pane);
            try {
                renderMathInElement(pane, { delimiters: KATEX_DELIMITERS, throwOnError: false });
            } catch (e) {
                console.warn('MarkdownEditorService: KaTeX render failed', e);
            }
        }
    }

    /**
     * Post-process a saved/displayed page's already-rendered static HTML (wiki view,
     * notebook view, tracker-item markdown) with the same Prism highlighting +
     * Mermaid diagram + KaTeX math treatment the editor's own panes get from
     * {@link enhanceContainer}. Kept as a separate method rather than reusing
     * `enhanceContainer`/`enhancePane` because those are hard-scoped to
     * `.toastui-editor-contents` and ToastUI's own `lang-xxx`-on-`<pre>` code-block
     * class convention — static HTML from CommonMark (backend wiki render) or
     * `marked` (ngx-markdown/notebook) instead puts `language-xxx` on `<code>`, and
     * has no `<div class="mermaid">` conversion of its own at all.
     *
     * No copy-button wiring needed here for ANY language, Mermaid included: the
     * `prismjs/plugins/toolbar` + `prismjs/plugins/copy-to-clipboard` imports at the
     * top of this file already auto-attach a copy button to every element
     * `Prism.highlightAllUnder` touches, unconditionally — including elements with no
     * registered grammar (Prism just falls back to escaping the text; it still runs
     * the 'complete' hook the toolbar plugin listens on). `convertMermaidFencesForStaticView`
     * relies on exactly that to give Mermaid blocks the same native button as every
     * other code block, instead of building a second, differently-styled one.
     *
     * Order matters: `Prism.highlightAllUnder` must run BEFORE the Mermaid conversion
     * step, not after — ngx-markdown's `<markdown>` component (used by Notebook/
     * tracker-item views) already runs its own `Prism.highlightAllUnder` pass
     * internally, synchronously, before this method ever gets a chance to run (see
     * `MarkdownService.render()`/`highlight()` in ngx-markdown — it's unconditional,
     * not opt-in, and uses this same global `Prism` instance). Running our own pass
     * first keeps both paths consistent: either way, by the time
     * `convertMermaidFencesForStaticView` runs, Prism has already wrapped the Mermaid
     * `<pre>` in its own `.code-toolbar` + native button, so there's nothing left to
     * build manually — just hide the source and drop the rendered diagram in
     * alongside it.
     *
     * Safe to call repeatedly — `convertMermaidFencesForStaticView` and Mermaid's
     * own `data-processed` marker both skip already-processed nodes.
     */
    public enhanceStaticHtml(container: HTMLElement | null | undefined): void {
        if (!container) return;
        this.restoreLineHighlightRanges(container);
        this.markPlainCodeBlocks(container);
        this.applyLineNumbers(container);
        try {
            Prism.highlightAllUnder(container);
        } catch (e) {
            console.warn('MarkdownEditorService: Prism highlight failed', e);
        }
        this.convertMermaidFencesForStaticView(container);
        this.renderMermaidBlocks(container);
        this.normalizeDisplayMathLineBreaks(container);
        try {
            renderMathInElement(container, { delimiters: KATEX_DELIMITERS, throwOnError: false });
        } catch (e) {
            console.warn('MarkdownEditorService: KaTeX render failed', e);
        }
    }

    /**
     * Turn every unprocessed `<pre><code class="language-mermaid">` block (the shape
     * CommonMark's `FencedCodeRenderer` and `marked` both produce) into a rendered
     * diagram: hide the source `<pre>` and drop a sibling `<div class="mermaid">`
     * next to it, which `renderMermaidBlocks` (below) already knows how to turn into
     * an SVG via `mermaid.init()`.
     *
     * By the time this runs (see {@link enhanceStaticHtml}'s docblock on ordering),
     * Prism has normally already wrapped the `<pre>` in its own `.code-toolbar` div
     * with a native copy button — appending the preview as a sibling of `<pre>`
     * (i.e. into `pre.parentElement`, whatever that currently is) lands it inside
     * that same wrapper, next to the button, without building a separate one, so
     * the button stays Prism's own (consistent look, no risk of a second one). If
     * Prism didn't wrap it for some reason (e.g. Prism unavailable), `pre.parentElement`
     * is just the original container and the preview lands as a plain sibling there.
     */
    private convertMermaidFencesForStaticView(container: HTMLElement): void {
        container.querySelectorAll<HTMLElement>('pre > code.language-mermaid').forEach((code) => {
            const pre = code.parentElement as HTMLElement | null;
            const parent = pre?.parentElement;
            if (!pre || !parent) return;
            if (parent.querySelector(':scope > .mermaid')) return; // already converted

            pre.classList.add('gf-mermaid-source');
            const preview = document.createElement('div');
            preview.className = 'mermaid';
            preview.textContent = code.textContent || '';
            parent.appendChild(preview);
        });
    }

    /**
     * Put back the `data-line` attribute Angular's HTML sanitizer stripped, reading
     * it off the {@link LINE_HIGHLIGHT_CLASS_PREFIX} class `gfMarkedOptionsFactory`
     * emits alongside it (see that constant's docblock for why the attribute alone
     * doesn't survive). Runs before the `Prism.highlightAllUnder` pass in
     * {@link enhanceStaticHtml}, which is what triggers the Line Highlight plugin.
     *
     * A no-op on surfaces whose HTML never went through the sanitizer — the wiki
     * view's backend CommonMark output carries a real `data-line` and no marker
     * class, and an already-restored `<pre>` is left alone on re-entry.
     */
    private restoreLineHighlightRanges(container: HTMLElement): void {
        container.querySelectorAll<HTMLElement>(`pre[class*="${LINE_HIGHLIGHT_CLASS_PREFIX}"]`).forEach((pre) => {
            if (pre.hasAttribute('data-line')) return;
            const marker = Array.from(pre.classList).find((c) => c.startsWith(LINE_HIGHLIGHT_CLASS_PREFIX));
            const lines = marker ? marker.slice(LINE_HIGHLIGHT_CLASS_PREFIX.length) : '';
            if (lines) pre.setAttribute('data-line', lines);
        });
    }

    /**
     * Tag a fence that declared no language with Prism's own `language-none`, so it
     * gets the same treatment as every other code block on the page.
     *
     * Both markdown renderers emit a bare `<pre><code>` for ```` ``` ```` with no
     * language (so does an indented code block), and everything downstream keys off
     * a `language-xxx` class: `Prism.highlightAllUnder` skips the block entirely, so
     * it gets no Prism background, no line numbers, and no copy button, while the
     * ToastUI editor panes style a language-less block like any other. That
     * asymmetry is what made a language-less fence read as ordinary body text in the
     * wiki view but as a code block in the editor that produced it. `language-none`
     * is Prism's documented opt-out token: it wires up the chrome (the `complete`
     * hook the toolbar/copy-button plugins listen on still fires) without trying to
     * tokenize the contents, and Prism copies the class onto the parent `<pre>`
     * itself, which is what the background and `overflow-x` rules select on.
     *
     * Must run before {@link applyLineNumbers}, which looks for exactly that class.
     */
    private markPlainCodeBlocks(container: HTMLElement): void {
        container.querySelectorAll<HTMLElement>('pre > code').forEach((code) => {
            const pre = code.parentElement;
            if (!pre) return;
            // ToastUI puts `lang-xxx` on the <pre>, CommonMark/marked put
            // `language-xxx` on the <code> — a block already carrying either is
            // Prism's to handle, and Mermaid fences must keep their own language.
            if (/(?:^|\s)lang(?:uage)?-/.test(code.className) || /(?:^|\s)lang(?:uage)?-/.test(pre.className)) return;
            code.classList.add('language-none');
        });
    }

    private applyLineNumbers(pane: HTMLElement): void {
        // ToastUI's own codeBlock HTML puts the class on <pre> (as `lang-xxx`), not on
        // <code> (as `language-xxx`, the marked/read-view convention) — match both.
        const pres = new Set<HTMLElement>();
        pane.querySelectorAll<HTMLElement>('pre[class*="lang-"], pre[class*="language-"]').forEach((pre) => pres.add(pre));
        // Static (non-editor) rendered HTML — CommonMark/marked put the `language-xxx` class on
        // <code>, not <pre> (see enhanceStaticHtml) — so also walk up from there.
        pane.querySelectorAll<HTMLElement>('pre > code[class*="language-"]').forEach((code) => {
            const pre = code.parentElement;
            if (pre) pres.add(pre);
        });
        pres.forEach((pre) => {
            if (!pre.classList.contains('line-numbers')) {
                pre.classList.add('line-numbers');
            }
        });
    }

    /**
     * Mermaid measures real rendered text/edge geometry to lay a diagram out. When a
     * pane is hidden (e.g. ToastUI's inactive "Preview" tab in tab-style Markdown mode
     * — it stays in the DOM, just `display: none`), everything measures as zero and
     * Mermaid's internal dagre edge-routing throws ("Could not find a suitable point
     * for the given distance") instead of silently no-op'ing. Defer rendering until
     * the pane is actually visible, using a `ResizeObserver` to catch the moment it
     * becomes so (e.g. the user clicks that Preview tab) since nothing else notifies
     * us of that — no content change occurs, so `enhanceContainer` wouldn't otherwise
     * be re-invoked.
     */
    private renderMermaidBlocks(pane: HTMLElement): void {
        if (pane.offsetParent === null) {
            this.deferMermaidUntilVisible(pane);
            return;
        }
        const nodes = pane.querySelectorAll('div.mermaid:not([data-processed])');
        if (!nodes.length) return;
        // mermaid.init() returns a Promise (it's async internally, despite the
        // deprecated callback-style signature) — a plain try/catch around the
        // call site does nothing for a rejection that happens after the
        // function has already returned, which is exactly why an invalid
        // diagram here surfaced as an unhandled promise rejection instead of
        // going through this catch.
        try {
            Promise.resolve(mermaid.init(undefined, nodes)).catch((e: any) => {
                console.warn('MarkdownEditorService: mermaid render failed', e);
            });
        } catch (e) {
            console.warn('MarkdownEditorService: mermaid render failed', e);
        }
    }

    private deferMermaidUntilVisible(pane: HTMLElement): void {
        if (this.mermaidPendingPanes.has(pane) || typeof ResizeObserver === 'undefined') return;
        this.mermaidPendingPanes.add(pane);
        const observer = new ResizeObserver(() => {
            if (pane.offsetParent === null) return;
            observer.disconnect();
            this.mermaidPendingPanes.delete(pane);
            this.renderMermaidBlocks(pane);
        });
        observer.observe(pane);
    }

    /**
     * Both panes: a `$$` / ...lines... / `$$` run can also land as sibling text nodes
     * inside a *single* paragraph, separated by literal `<br>` elements — this is how
     * the static Markdown-preview pane renders every markdown soft line break (see
     * ToastUI's toHTML `softbreak` convertor), and it defeats auto-render exactly like
     * the cross-paragraph case above. Collapse each affected paragraph's `<br>`-joined
     * lines into one text node joined by `\n`. Idempotent — once collapsed, the
     * paragraph has no more `<br>` children, so a later pass is a no-op.
     */
    private normalizeDisplayMathLineBreaks(pane: HTMLElement): void {
        pane.querySelectorAll<HTMLElement>('p').forEach((p) => {
            if (!p.querySelector('br')) return;
            const lines: string[] = [''];
            p.childNodes.forEach((child) => {
                if (child.nodeName === 'BR') {
                    lines.push('');
                } else {
                    lines[lines.length - 1] += child.textContent || '';
                }
            });
            const startIdx = lines.findIndex((line) => line.trim() === '$$');
            if (startIdx === -1) return;
            const endIdx = lines.findIndex((line, idx) => idx > startIdx && line.trim() === '$$');
            if (endIdx === -1) return;
            p.textContent = lines.join('\n');
        });
    }

    /**
     * Lazily create the single Clipboard.js instance backing every WYSIWYG code
     * block's copy button (each button is created directly by `GfCodeBlockView`,
     * not swept in after the fact — see that class below). Reads the code text
     * live from the DOM at click time rather than a cached attribute, so it's
     * always correct even though the button and its code text can be created at
     * different times (e.g. a Mermaid block's copy button exists from the start,
     * but its `<code>` text is the same static source throughout anyway).
     */
    public ensureClipboard(): void {
        if (this.clipboard) return;
        this.clipboard = new Clipboard('.gf-code-copy-btn', {
            text: (trigger: Element) =>
                trigger.closest('.toastui-editor-ww-code-block')?.querySelector('code')?.textContent || '',
        });
        this.clipboard.on('success', (e: any) => this.flashCopied(e.trigger));
        this.clipboard.on('error', (e: any) => console.warn('MarkdownEditorService: clipboard copy failed', e));
    }

    private flashCopied(button: HTMLElement): void {
        const original = button.textContent;
        button.classList.add('gf-code-copy-btn-copied');
        button.textContent = 'Copied!';
        setTimeout(() => {
            button.classList.remove('gf-code-copy-btn-copied');
            button.textContent = original || 'Copy';
        }, 1500);
    }

    /**
     * Collapse editor-specific section names into the shared namespace used for
     * persisted markdown editor mode preferences.
     */
    public resolvePreferenceSection(preferenceSection: string, uploadSection: string, fallbackSection: string): string {
        const rawSection = (preferenceSection || uploadSection || '').toLowerCase();
        if (!rawSection) return fallbackSection;
        if (rawSection.indexOf('trackeritem') === 0) return 'trackeritem';
        if (rawSection.indexOf('tracker') === 0) return 'tracker';
        if (rawSection === 'wikipage' || rawSection === 'wikifiles') return 'wiki';
        return rawSection;
    }

    /**
     * Build the user preference key used to store the last selected editor mode.
     */
    public getModePreferenceName(preferenceSection: string, uploadSection: string, fallbackSection: string): string {
        return 'mdeditor_' + this.resolvePreferenceSection(preferenceSection, uploadSection, fallbackSection);
    }

    /**
     * Normalize persisted or external mode values onto the caller's markdown and
     * WYSIWYG mode tokens, preserving the provided initial mode as fallback.
     */
    public normalizeMode<T extends string>(value: any, initialMode: T, markdownMode: T, wysiwygMode: T): T {
        const normalized = String(value || '').toLowerCase();
        if (normalized === 'raw' || normalized === String(markdownMode).toLowerCase()) {
            return markdownMode;
        }
        if (normalized === String(wysiwygMode).toLowerCase()) {
            return wysiwygMode;
        }
        return initialMode;
    }

    /**
     * Heuristically detect pasted markdown so WYSIWYG editors can switch to raw
     * mode before ToastUI escapes the source.
     */
    public looksLikePastedMarkdown(text: string): boolean {
        const source = text.trim();
        if (!source) return false;

        const patterns = [
            /^#{1,6}\s+/m,
            /^\s*[-*+]\s+/m,
            /^\s*\d+\.\s+/m,
            /\[.+?\]\(.+?\)/,
            /!\[.+?\]\(.+?\)/,
            /`{3}[\s\S]*?`{3}/,
            /^\s*>\s+/m,
            /(^|[^*])\*\*[^\n*]+\*\*($|[^*])/m,
            /(^|[^_])__[^\n_]+__($|[^_])/m,
            /^\|.+\|$/m,
        ];

        const score = patterns.reduce((count, pattern) => count + (pattern.test(source) ? 1 : 0), 0);
        if (score >= 2) return true;

        return score === 1 && source.indexOf('\n') !== -1 && /(^#{1,6}\s+)|(^\s*[-*+]\s+)|(^\s*\d+\.\s+)|(`{3})/m.test(source);
    }

    /**
     * Heuristically detect pasted text whose whitespace carries meaning, so
     * WYSIWYG editors can take the same raw-mode escape hatch as pasted markdown.
     *
     * Three signals, each chosen to fire on layout and stay quiet on prose:
     *   - any tab character
     *   - a line indented by two or more spaces
     *   - three or more spaces between two non-space characters
     *
     * The three-space threshold is deliberate: two spaces after a full stop is a
     * common typing convention and must not drag the user out of WYSIWYG, while
     * three or more between words only occurs in aligned columns.
     */
    public looksLikePreformattedText(text: string): boolean {
        if (!text || !text.trim()) return false;

        return /\t/.test(text)              // tabs
            || /^ {2,}\S/m.test(text)       // indented line
            || /\S {3,}\S/.test(text);      // aligned columns
    }

    /**
     * Strip standalone `<br>` lines that ToastUI emits when converting empty
     * WYSIWYG paragraphs back into markdown.
     */
    public cleanBrTags(md: string): string {
        if (!md || !md.includes('<br')) return md;
        return md
            .replace(/^[ \t]*<br\s*\/?>[ \t]*$/gim, '')
            .replace(/\n{3,}/g, '\n\n')
            .trim();
    }

    /**
     * Coordinate markdown paste handling from WYSIWYG mode: detect markdown,
     * suppress ToastUI's native paste path, switch to raw mode, and insert the
     * pasted source once the markdown editor is ready.
     */
    public handleMarkdownPasteSwitch<TMode extends string>(options: HandleMarkdownPasteOptions<TMode>): boolean {
        if (!options.editor || options.readOnly || options.currentMode === options.markdownMode || options.isUpdating) {
            return false;
        }

        const clipboard = options.event.clipboardData;
        const pastedText = (clipboard && clipboard.getData('text/plain')) || '';
        if (!pastedText.trim()) {
            return false;
        }
        // Markdown needs raw mode so ToastUI does not AST-escape it; preformatted
        // text needs it so ProseMirror does not collapse its whitespace.
        if (!this.looksLikePastedMarkdown(pastedText) && !this.looksLikePreformattedText(pastedText)) {
            return false;
        }

        options.event.preventDefault();
        if (typeof options.event.stopImmediatePropagation === 'function') {
            options.event.stopImmediatePropagation();
        }

        const existingMarkdown = this.cleanBrTags(options.editor.getMarkdown() || '');
        options.setUpdating(true);
        try {
            options.switchToMarkdownMode();
        } catch (error) {
            options.setUpdating(false);
            if (options.onModeSwitchError) {
                options.onModeSwitchError(error);
            }
            return true;
        }

        this.insertPastedMarkdownAfterModeSwitch({
            editor: options.editor,
            pastedText,
            existingMarkdown,
            remainingFrames: 3,
            isDestroyed: options.isDestroyed,
            isEditorInMarkdownMode: options.isEditorInMarkdownMode,
            setUpdating: options.setUpdating,
            onAfterInsert: options.onAfterInsert,
            onError: options.onInsertError,
        });

        return true;
    }

    /**
     * Wait for the editor to finish switching into markdown mode, then inject the
     * pasted markdown via `setMarkdown()` so ToastUI does not AST-escape it.
     */
    public insertPastedMarkdownAfterModeSwitch(options: MarkdownInsertAfterModeSwitchOptions): void {
        requestAnimationFrame(() => {
            if (!options.editor || options.isDestroyed()) {
                options.setUpdating(false);
                return;
            }

            if (!options.isEditorInMarkdownMode() && options.remainingFrames > 0) {
                this.insertPastedMarkdownAfterModeSwitch({
                    ...options,
                    remainingFrames: options.remainingFrames - 1,
                });
                return;
            }

            try {
                const separator = options.existingMarkdown.trim() ? '\n\n' : '';
                options.editor.setMarkdown(options.existingMarkdown + separator + options.pastedText);
            } catch (error) {
                if (options.onError) {
                    options.onError(error);
                }
            }

            options.setUpdating(false);
            if (options.onAfterInsert) {
                options.onAfterInsert();
            }
        });
    }

    /**
     * Re-read markdown on the next frame after a manual mode switch and remove
     * ToastUI's standalone `<br>` serialization artifacts if they were introduced.
     */
    public cleanupBrTagsAfterModeSwitch(options: CleanupBrTagsAfterModeSwitchOptions): void {
        requestAnimationFrame(() => {
            if (!options.editor || options.isDestroyed() || options.isUpdating()) return;
            const markdown = options.editor.getMarkdown() || '';
            const cleaned = this.cleanBrTags(markdown);
            if (cleaned === markdown) return;

            options.setUpdating(true);
            try {
                options.editor.setMarkdown(cleaned);
            } catch (error) {
                if (options.onError) {
                    options.onError(error);
                }
            }
            options.setUpdating(false);
        });
    }
}